import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import cx from 'classnames';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomerNumberDropdown from '@app/components/common/orderInformationFields/CustomerNumberDropdown';
import StoreLocationDropdown from '@app/components/common/orderInformationFields/StoreLocationDropdown';
import EndUseCodeDropdown from '@app/components/common/orderInformationFields/EndUseCodeDropdown';
import OrderType from '@app/components/common/orderInformationFields/OrderType';
import { ComponentLoader, InlineLoader } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { AFFILIATION_DEALER, STATUS } from '@app/constants/commonConstants';
import UpdateCartCancelButtons from './UpdateCartCancelButtons';
import styles from './EditOrderInformationForm.module.scss';
import EditOrderInformationFormExceptionFeedback from './EditOrderInformationFormExceptionFeedback';
import EditOrderInformationFormEffects from './EditOrderInformationFormEffects';
import { setOrderInformation } from '@app/store/checkout/actions';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import CustomerNumberLoader from './CustomerNumberLoader';
import { getFormDisplayInfo, getOrderTypeInfo } from '../utils';
import { EDIT_ORDER_INFORMATION_MODAL_ID } from '../constants';

const EditOrderInformationForm = ({ closeModal }) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const dealerAssociationStatus = useSelector(s => s.dealerAssociation.status);
  const dealerAssociationCallError =
    dealerAssociationStatus === STATUS.REJECTED;
  const dealerAssociationCallLoading =
    dealerAssociationStatus === STATUS.PENDING;

  const dealerCustomerStoresStatus = useSelector(
    s => s.dealerCustomer.getDealerCustomerStoresStatus
  );
  const dealerCustomerStoresLoading =
    dealerCustomerStoresStatus === STATUS.PENDING ||
    dealerCustomerStoresStatus === STATUS.IDLE;
  const dealerCustomerServiceError =
    dealerCustomerStoresStatus === STATUS.REJECTED;

  const orderInformationStatus = useSelector(
    s => s.checkout.setOrderInformationStatus
  );
  const orderInformationLoading = orderInformationStatus === STATUS.PENDING;
  const orderInformationError = orderInformationStatus === STATUS.REJECTED;

  const storeId = useSelector(s => s.common.storeId);
  const customerType = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData?.customerType
  );
  const isNotIAUser = customerType && customerType !== 'IA';
  const dealerAssociations =
    useSelector(s => s.dealerAssociation.dealerAssociations) || [];
  const selectedCustomerNumber = useSelector(
    s => s.common.selectedCustomerNumber
  );
  const selectedCustomerName = useSelector(s => s.common.selectedCustomerName);
  const selectedDealerAssociation = dealerAssociations.find(
    da => da.value === storeId
  );
  const selectedEndUseCode = useSelector(s => s.common.selectedEndUseCode);
  const selectedOrderType = useSelector(s => s.common.selectedOrderType);
  const selectedStore = useSelector(s => s.common.selectedStore);
  const dcnData = useSelector(s => s.dealerCustomer.byId);
  const selectedStoreLocation = dcnData?.[selectedCustomerNumber]?.store?.find(
    st => st.value === selectedStore
  );
  const initialCustomerNumber = {
    label: `${selectedCustomerNumber}-${selectedCustomerName}`,
    name: selectedCustomerName,
    value: selectedCustomerNumber
  };
  const selectedDealer = useSelector(s => s.common?.selectedDealer);
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const endUseCodePlaceholderText = t(
    'CAT_SHOPPING_PREFERENCES_SEL_END_USE_CODE'
  );
  let initialValues = {
    dealer: isDealerUser ? selectedDealer : selectedDealerAssociation,
    customerNumber: initialCustomerNumber,
    endUseCode: selectedEndUseCode ?? {
      label: endUseCodePlaceholderText,
      value: ''
    },
    orderType: selectedOrderType?.value,
    storeLocation: selectedStoreLocation
  };

  const getOrderType = values => {
    const defaultStdLabel = t('CAT_MA_STANDARD');
    const defaultStockLabel = t('CAT_MA_STOCK');
    const ORDER_TYPE_STANDARD = 'STD';
    const { orderTypes } = getOrderTypeInfo(values);
    const orderType = values.orderType;
    const formSelectedOrderType = orderTypes?.find(o => o.value === orderType);

    return {
      label:
        formSelectedOrderType?.label ??
        (orderType === ORDER_TYPE_STANDARD
          ? defaultStdLabel
          : defaultStockLabel),
      value: orderType
    };
  };

  const handleSubmit = values => {
    const { customerNumber = {}, endUseCode = {}, storeLocation = {} } = values;
    const errorInfo = {
      domain: ERROR_DOMAIN.CART,
      path: ERROR_PATH.EDIT_ORDER_INFORMATION
    };

    const formattedValues = {
      customerNumber: customerNumber.label,
      endUseCode: endUseCode?.value
        ? `${endUseCode.value}:${endUseCode.label}`
        : null,
      errorInfo,
      orderType: getOrderType(values),
      storeLocation: storeLocation.value,
      isOrderInfoUpdate: true
    };

    dispatch(setOrderInformation(formattedValues));
  };

  // Override overflow styles to fix dropdown overflow issue in Order Information modal
  // This is done inside its only child to ensure the modal exists, since it will be opened
  useEffect(() => {
    const orderInformationModal = document.getElementById(
      EDIT_ORDER_INFORMATION_MODAL_ID
    );
    orderInformationModal?.style?.setProperty?.(
      'overflow',
      'visible',
      'important'
    );
  }, []);

  const EDIT_ORDER_INFORMATION_ID_PREFIX = 'edit-order-information';
  const hasError = !!(dealerCustomerServiceError || dealerAssociationCallError);

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnChange={false}
    >
      {({ values }) => {
        const stores = dcnData?.[values.customerNumber?.value]?.store;
        const {
          customerNumbers,
          endUseCodes,
          orderTypes,
          showEndUseCodeDropdown,
          showOrderTypes,
          showStoreLocationDropDown
        } = getFormDisplayInfo({
          isLoggedIn: true,
          storeLocations: stores,
          values
        });

        const displayCustomerNumbers =
          dealerAssociationCallError || isDealerUser
            ? [initialCustomerNumber]
            : customerNumbers;

        const mainFormContent = dealerCustomerStoresLoading ? (
          <InlineLoader
            className={cx(
              'd-flex',
              'align-items-center',
              styles['dropDownHeight']
            )}
          />
        ) : (
          <>
            {(showStoreLocationDropDown ||
              dealerCustomerServiceError ||
              (dealerAssociationCallError && stores?.length)) && (
              <StoreLocationDropdown
                error={dealerCustomerServiceError}
                errorText={t('LOAD_STORE_DATA_FAIL')}
                idPrefix={EDIT_ORDER_INFORMATION_ID_PREFIX}
                labelText={t('MA_OH_STORE_LOC')}
                storeLocations={dealerCustomerServiceError ? [] : stores}
              />
            )}
            {showEndUseCodeDropdown && !dealerCustomerServiceError && (
              <EndUseCodeDropdown endUseCodes={endUseCodes} />
            )}
            {showOrderTypes && !dealerCustomerServiceError && (
              <OrderType
                idPrefix={EDIT_ORDER_INFORMATION_ID_PREFIX}
                orderTypes={orderTypes}
              />
            )}
          </>
        );
        return (
          <>
            {orderInformationLoading && <ComponentLoader />}
            <EditOrderInformationFormExceptionFeedback
              dealerAssociationCallError={dealerAssociationCallError}
              dealerCustomerServiceCallError={dealerCustomerServiceError}
              orderInformationError={orderInformationError}
              onRetrySetOrderinformation={handleSubmit}
            />
            <p className="text-md u-color--DarkGray mb-3 pb-1">
              {t('ORDER_INFOR_CHANGE_MODAL')}
            </p>
            <Form>
              {dealerAssociationCallLoading ? (
                <CustomerNumberLoader />
              ) : (
                <>
                  {(isDealerUser || isNotIAUser) && (
                    <CustomerNumberDropdown
                      customerNumbers={displayCustomerNumbers}
                      idPrefix={EDIT_ORDER_INFORMATION_ID_PREFIX}
                      itemDisplayFormat={({ name, value, label }) =>
                        name && value ? `${value}-${name}` : label
                      }
                      labelText={t('FOOTER_ACCOUNT')}
                    />
                  )}
                  {mainFormContent}
                </>
              )}
              <EditOrderInformationFormEffects hasError={hasError} />
              <UpdateCartCancelButtons
                closeModal={closeModal}
                disableUpdateButton={
                  dealerAssociationCallLoading ||
                  dealerCustomerStoresLoading ||
                  dealerCustomerServiceError
                }
              />
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export default EditOrderInformationForm;

EditOrderInformationForm.propTypes = {
  closeModal: PropTypes.func.isRequired
};
